import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Delete,
  Search,
  Refresh,
  Folder,
  InsertDriveFile,
  Edit,
  Email,
} from '@mui/icons-material';
import { documentsApi } from '../services/api';
import { Document, DocumentUpload } from '../types';
import { useAuth } from '../context/AuthContext';

// UploadForm component - defined outside to prevent recreation on parent re-render
const UploadForm: React.FC<{
  onSubmit: (data: DocumentUpload) => void;
  onCancel: () => void;
  loading: boolean;
  uploadFile: File | null;
  formatFileSize: (bytes: number) => string;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ onSubmit, onCancel, loading, uploadFile, formatFileSize, handleFileChange }) => {
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    is_public: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Button
        variant="outlined"
        component="label"
        startIcon={<CloudUpload />}
        fullWidth
        sx={{ mb: 2, py: 2 }}
      >
        {uploadFile ? `${uploadFile.name} (${formatFileSize(uploadFile.size)})` : 'Select File'}
        <input
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.bmp"
          type="file"
          hidden
          onChange={handleFileChange}
          required
        />
      </Button>

      <TextField
        fullWidth
        label="Category"
        value={formData.category}
        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        margin="normal"
        helperText="e.g., HR Policies, Forms, Training Materials"
      />

      <TextField
        fullWidth
        label="Description"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        margin="normal"
        multiline
        rows={3}
        helperText="Brief description of the document"
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.is_public}
            onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
          />
        }
        label="Make document accessible to all users"
        sx={{ mt: 2 }}
      />

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !uploadFile}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Upload'}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          fullWidth
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

const Documents: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const { isManager } = useAuth();
  const queryClient = useQueryClient();

  const { data: documentsData, isLoading, refetch } = useQuery(
    ['documents', searchTerm, categoryFilter],
    () => documentsApi.getDocuments({
      search: searchTerm || undefined,
      category: categoryFilter !== 'all' ? categoryFilter : undefined,
    }),
    {
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to fetch documents');
      }
    }
  );

  const { data: categories } = useQuery('categories', documentsApi.getCategories);

  const uploadMutation = useMutation(
    ({ file, metadata }: { file: File; metadata: DocumentUpload }) =>
      documentsApi.uploadDocument(file, metadata),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        queryClient.invalidateQueries('categories');
        setUploadDialogOpen(false);
        setUploadFile(null);
        setError('');
      },
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to upload document');
      }
    }
  );

  const updateMutation = useMutation(
    ({ documentId, data }: { documentId: number; data: Partial<DocumentUpload> }) =>
      documentsApi.updateDocument(documentId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        queryClient.invalidateQueries('categories');
        setEditDialogOpen(false);
        setSelectedDocument(null);
        setError('');
      },
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to update document');
      }
    }
  );

  const deleteMutation = useMutation(documentsApi.deleteDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      setError('');
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Failed to delete document');
    }
  });

  const emailMutation = useMutation(documentsApi.emailDocument, {
    onSuccess: () => {
      setSuccess('Document sent via email successfully!');
      setError('');
      setTimeout(() => setSuccess(''), 5000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Failed to send document via email');
    }
  });

  const handleUpload = (metadata: DocumentUpload) => {
    if (uploadFile) {
      uploadMutation.mutate({ file: uploadFile, metadata });
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const blob = await documentsApi.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to download document');
    }
  };

  const handleDelete = (documentId: number) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteMutation.mutate(documentId);
    }
  };

  const handleEmail = (documentId: number) => {
    if (window.confirm('Send this document via email?')) {
      emailMutation.mutate(documentId);
    }
  };

  const handleUpdate = (data: Partial<DocumentUpload>) => {
    if (selectedDocument) {
      updateMutation.mutate({ documentId: selectedDocument.id, data });
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.includes('pdf')) return '📄';
    if (contentType.includes('word')) return '📝';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
    return '📁';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const EditForm: React.FC<{
    document: Document;
    onSubmit: (data: Partial<DocumentUpload>) => void;
    onCancel: () => void;
    loading: boolean;
  }> = ({ document, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState({
      category: document.category || '',
      description: document.description || '',
      is_public: document.is_public,
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          margin="normal"
          multiline
          rows={3}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.is_public}
              onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
            />
          }
          label="Make document accessible to all users"
          sx={{ mt: 2 }}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            fullWidth
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          HR Documents Repository
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories?.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Document
          </Button>
          <IconButton onClick={() => refetch()}>
            <Refresh />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {documentsData?.documents?.map((document: Document) => (
              <Grid item xs={12} sm={6} md={4} key={document.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h2" sx={{ mr: 1 }}>
                        {getFileIcon(document.content_type)}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2" noWrap>
                          {document.original_filename}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatFileSize(document.file_size)}
                        </Typography>
                      </Box>
                    </Box>

                    {document.category && (
                      <Chip
                        label={document.category}
                        size="small"
                        sx={{ mb: 1 }}
                        icon={<Folder />}
                      />
                    )}

                    {document.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {document.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        label={document.is_public ? 'Public' : 'Private'}
                        size="small"
                        color={document.is_public ? 'success' : 'default'}
                      />
                    </Box>

                    <Typography variant="caption" color="textSecondary">
                      Uploaded: {new Date(document.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                    </Typography>
                  </CardContent>

                  <Divider />

                  <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownload(document)}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleEmail(document.id)}
                        disabled={emailMutation.isLoading}
                      >
                        Email
                      </Button>
                    </Box>
                    {isManager && (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDocument(document);
                            setEditDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(document.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {documentsData?.documents?.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <InsertDriveFile sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No documents found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Upload your first document to get started
            </Typography>
          </Box>
        )}
      </Box>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <UploadForm
            onSubmit={handleUpload}
            onCancel={() => {
              setUploadDialogOpen(false);
              setUploadFile(null);
            }}
            loading={uploadMutation.isLoading}
            uploadFile={uploadFile}
            formatFileSize={formatFileSize}
            handleFileChange={handleFileChange}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <EditForm
              document={selectedDocument}
              onSubmit={handleUpdate}
              onCancel={() => {
                setEditDialogOpen(false);
                setSelectedDocument(null);
              }}
              loading={updateMutation.isLoading}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Documents;